/**
 * Single source of truth for care specialties used across:
 * - Onboarding wizard
 * - Lead Filters (scoring + priority)
 * - Find Referrals
 *
 * Keeping these aligned ensures specialty-based scoring, filtering and
 * referral generation all reference the same labels.
 */
import {
  Brain, Activity, Heart, Baby, Shield, Moon, Clock, Flag, UserCheck,
  Stethoscope, Hand, Users, MessageSquare, type LucideIcon,
} from "lucide-react";

export interface SpecialtyOption {
  label: string;
  icon: LucideIcon;
}

/** Home-care specialties (canonical clinical list). */
export const HC_SPECIALTIES: SpecialtyOption[] = [
  { label: "Alzheimer's/Dementia", icon: Brain },
  { label: "Post-Surgical Recovery", icon: Activity },
  { label: "Chronic Disease Management", icon: UserCheck },
  { label: "Palliative/Hospice Care", icon: Moon },
  { label: "Pediatric Home Care", icon: Baby },
  { label: "Mental Health Support", icon: Shield },
  { label: "Physical Therapy", icon: Activity },
  { label: "Personal Care (ADLs)", icon: Hand },
  { label: "Companion Care", icon: Heart },
  { label: "Skilled Nursing", icon: Stethoscope },
  { label: "Occupational Therapy", icon: Clock },
  { label: "Medical Social Work", icon: Users },
  { label: "Speech Therapy", icon: MessageSquare },
];

/** Group-home / waiver specialties. */
export const GH_SPECIALTIES: SpecialtyOption[] = [
  { label: "Intellectual Disability (ID)", icon: Brain },
  { label: "Developmental Disability (DD)", icon: Activity },
  { label: "Mental Health (MH)", icon: Shield },
  { label: "Dual Diagnosis (ID/MH)", icon: Heart },
  { label: "Autism Spectrum Disorder", icon: UserCheck },
  { label: "Veterans with Disabilities", icon: Flag },
  { label: "Traumatic Brain Injury (TBI)", icon: Moon },
  { label: "Transition-Age Youth", icon: Baby },
  { label: "Crisis Stabilization", icon: Clock },
  { label: "Medically Complex Needs", icon: Stethoscope },
];

/** Convenience: just the label strings. */
export const HC_SPECIALTY_LABELS: string[] = HC_SPECIALTIES.map(s => s.label);
export const GH_SPECIALTY_LABELS: string[] = GH_SPECIALTIES.map(s => s.label);